import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { FilterState, FilterContext, FilterPreset, FilterMetadata, FilterOption, FilterPredicate, FilterSecurityContext, DateRange, FilterValue } from '../types/filters';
import { FILTER_DIMENSIONS, DEFAULT_FILTER_PRESETS } from '../config/filterDimensions';
import { UserRole } from '../types';

// Default state with proper initialization
const createInitialFilterState = (): FilterState => ({
  dateRange: {
    start: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
    end: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
  },
  country: [],
  region: [],
  businessUnit: [],
  department: [],
  function: [],
  level: [],
  grade: [],
  employmentType: [],
  manager: [],
  team: [],
  gender: [],
  ageRange: null,
  tenure: [],
  tenureBands: [],
  performanceBand: [],
  attritionRiskBand: [],
  custom: {},
});

const initialState: FilterContext = {
  filters: createInitialFilterState(),
  metadata: {
    appliedFilters: [],
    resultCount: 0,
    totalCount: 0,
    isFiltered: false,
    lastUpdated: new Date(),
  },
  availableOptions: {},
  presets: DEFAULT_FILTER_PRESETS.map(p => ({
    ...p,
    createdAt: new Date(),
    updatedAt: new Date(),
  })),
  loading: false,
  error: null,
};

// Async thunk for loading filter options with cascading
export const loadFilterOptions = createAsyncThunk(
  'filters/loadOptions',
  async (params: { 
    dimension: string;
    currentFilters: Partial<FilterState>;
    securityContext: FilterSecurityContext;
  }) => {
    const { dimension, currentFilters, securityContext } = params;
    
    // Simulate API call with security filtering
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const dimensionConfig = FILTER_DIMENSIONS[dimension];
    let options = dimensionConfig.values || [];
    
    // Apply RBAC filtering
    options = applySecurityFiltering(options, dimension, securityContext);
    
    // Apply cascading filters
    if (dimensionConfig.dependsOn) {
      options = applyCascadingFilters(options, dimension, currentFilters, dimensionConfig.dependsOn);
    }
    
    return { dimension, options };
  }
);

// Async thunk for applying filters with performance tracking
export const applyFiltersAsync = createAsyncThunk(
  'filters/applyFilters',
  async (params: {
    filters: Partial<FilterState>;
    securityContext: FilterSecurityContext;
  }) => {
    const { filters, securityContext } = params;
    const startTime = Date.now();
    
    // Build filter predicates
    const predicates = buildFilterPredicates(filters);
    
    // Apply RBAC row-level filters
    const securityPredicates = securityContext.rowLevelFilters;
    const allPredicates = [...predicates, ...securityPredicates];
    
    // Simulate data filtering (in real app, this would be an API call)
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Mock result counting
    const totalCount = 2847; // Total employees
    const resultCount = Math.floor(totalCount * (0.3 + Math.random() * 0.6)); // Random filtering result
    
    const queryTime = Date.now() - startTime;
    
    return {
      resultCount,
      totalCount,
      queryTime,
      appliedFilters: Object.keys(filters).filter(key => {
        const value = filters[key as keyof FilterState];
        return value !== null && value !== undefined && 
               (Array.isArray(value) ? value.length > 0 : true);
      }),
    };
  }
);

// Async thunk for saving filter presets
export const saveFilterPreset = createAsyncThunk(
  'filters/savePreset',
  async (preset: Omit<FilterPreset, 'id' | 'createdAt' | 'updatedAt'>) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newPreset: FilterPreset = {
      ...preset,
      id: `preset_${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    return newPreset;
  }
);

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    // Set individual filter values
    setFilter: (state, action: PayloadAction<{ 
      dimension: keyof FilterState; 
      value: any;
    }>) => {
      const { dimension, value } = action.payload;
      (state.filters as any)[dimension] = value;
      state.metadata.lastUpdated = new Date();
    },
    
    // Set multiple filter values at once
    setFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.metadata.lastUpdated = new Date();
    },
    
    // Reset all filters to default
    resetFilters: (state) => {
      state.filters = createInitialFilterState();
      state.metadata.appliedFilters = [];
      state.metadata.isFiltered = false;
      state.metadata.lastUpdated = new Date();
    },
    
    // Reset specific filter
    resetFilter: (state, action: PayloadAction<keyof FilterState>) => {
      const dimension = action.payload;
      const defaultState = createInitialFilterState();
      (state.filters as any)[dimension] = (defaultState as any)[dimension];
      state.metadata.lastUpdated = new Date();
    },
    
    // Load filter preset
    loadPreset: (state, action: PayloadAction<string>) => {
      const presetId = action.payload;
      const preset = state.presets.find(p => p.id === presetId);
      if (preset) {
        state.filters = { ...createInitialFilterState(), ...preset.filters };
        state.metadata.lastUpdated = new Date();
      }
    },
    
    // Delete filter preset
    deletePreset: (state, action: PayloadAction<string>) => {
      const presetId = action.payload;
      state.presets = state.presets.filter(p => p.id !== presetId);
    },
    
    // Update available filter options
    updateFilterOptions: (state, action: PayloadAction<{
      dimension: string;
      options: FilterOption[];
    }>) => {
      const { dimension, options } = action.payload;
      state.availableOptions[dimension] = options;
    },
    
    // Cross-filter: add filter from visual interaction
    addCrossFilter: (state, action: PayloadAction<{
      dimension: keyof FilterState;
      value: string | string[];
    }>) => {
      const { dimension, value } = action.payload;
      const currentValue = state.filters[dimension];
      
      if (Array.isArray(currentValue) && typeof currentValue[0] === 'string') {
        const newValues = Array.isArray(value) ? value : [value];
        const combined = Array.from(new Set([...currentValue, ...newValues]));
        (state.filters as any)[dimension] = combined;
      } else {
        (state.filters as any)[dimension] = value;
      }
      
      state.metadata.lastUpdated = new Date();
    },
    
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  
  extraReducers: (builder) => {
    builder
      // Load filter options
      .addCase(loadFilterOptions.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadFilterOptions.fulfilled, (state, action) => {
        const { dimension, options } = action.payload;
        state.availableOptions[dimension] = options;
        state.loading = false;
      })
      .addCase(loadFilterOptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load filter options';
      })
      
      // Apply filters
      .addCase(applyFiltersAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyFiltersAsync.fulfilled, (state, action) => {
        const { resultCount, totalCount, queryTime, appliedFilters } = action.payload;
        state.metadata = {
          ...state.metadata,
          resultCount,
          totalCount,
          appliedFilters,
          isFiltered: appliedFilters.length > 0,
          lastUpdated: new Date(),
          performanceMetrics: {
            queryTime,
            cacheHit: queryTime < 100,
          },
        };
        state.loading = false;
      })
      .addCase(applyFiltersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to apply filters';
      })
      
      // Save preset
      .addCase(saveFilterPreset.fulfilled, (state, action) => {
        state.presets.push(action.payload);
      });
  },
});

// Helper functions
function applySecurityFiltering(
  options: FilterOption[], 
  dimension: string, 
  securityContext: FilterSecurityContext
): FilterOption[] {
  // Apply RBAC filtering based on user's permissions
  switch (dimension) {
    case 'country':
      return options.filter(opt => 
        securityContext.allowedCountries.includes(opt.value as string)
      );
    case 'businessUnit':
      return options.filter(opt => 
        securityContext.allowedBusinessUnits.includes(opt.value as string)
      );
    case 'department':
      return options.filter(opt => 
        securityContext.allowedDepartments.includes(opt.value as string)
      );
    default:
      return options;
  }
}

function applyCascadingFilters(
  options: FilterOption[],
  dimension: string,
  currentFilters: Partial<FilterState>,
  dependsOn: string[]
): FilterOption[] {
  // Apply cascading filter logic
  // This would implement the business logic for how filters depend on each other
  // For now, return all options (would be implemented based on actual data relationships)
  return options;
}

function buildFilterPredicates(filters: Partial<FilterState>): FilterPredicate[] {
  const predicates: FilterPredicate[] = [];
  
  Object.entries(filters).forEach(([field, value]) => {
    if (value === null || value === undefined) return;
    
    if (Array.isArray(value) && value.length > 0) {
      predicates.push({
        field,
        operation: 'in',
        value: value,
      });
    } else if (typeof value === 'object' && value && 'start' in value && 'end' in value) {
      // Date range
      predicates.push({
        field,
        operation: 'between',
        value: [value.start, value.end] as FilterValue[],
      });
    } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value instanceof Date) {
      predicates.push({
        field,
        operation: 'equals',
        value: value,
      });
    }
  });
  
  return predicates;
}

export const {
  setFilter,
  setFilters,
  resetFilters,
  resetFilter,
  loadPreset,
  deletePreset,
  updateFilterOptions,
  addCrossFilter,
  setLoading,
  setError,
} = filtersSlice.actions;

export default filtersSlice.reducer;

// Selectors
export const selectFilters = (state: { filters: FilterContext }) => state.filters.filters;
export const selectFilterMetadata = (state: { filters: FilterContext }) => state.filters.metadata;
export const selectFilterPresets = (state: { filters: FilterContext }) => state.filters.presets;
export const selectFilterLoading = (state: { filters: FilterContext }) => state.filters.loading;
export const selectAvailableOptions = (state: { filters: FilterContext }) => state.filters.availableOptions;

// Complex selectors
export const selectActiveFiltersCount = (state: { filters: FilterContext }) => {
  return state.filters.metadata.appliedFilters.length;
};

export const selectIsFiltered = (state: { filters: FilterContext }) => {
  return state.filters.metadata.isFiltered;
};

export const selectFilterSummary = (state: { filters: FilterContext }) => {
  const { filters, metadata } = state.filters;
  const summary: string[] = [];
  
  if (filters.dateRange) {
    summary.push(`Date: ${filters.dateRange.start.toLocaleDateString()} - ${filters.dateRange.end.toLocaleDateString()}`);
  }
  
  if (filters.country.length > 0) {
    summary.push(`Country: ${filters.country.join(', ')}`);
  }
  
  if (filters.businessUnit.length > 0) {
    summary.push(`Business Unit: ${filters.businessUnit.join(', ')}`);
  }
  
  if (filters.department.length > 0) {
    summary.push(`Department: ${filters.department.join(', ')}`);
  }
  
  return summary;
};